import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ApiError } from '../../api/client'
import { googleSheetsApi, parseSpreadsheetId } from '../../api/googleSheets'
import { importApi } from '../../api/imports'
import { Alert } from '../../components/Alert'
import { BbIcon } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { IMPORT_STEPS } from './steps'
import { formatBytes, formatInt } from '../../lib/format'
import { formatRelative } from '../../lib/format'
import { useImportWizard } from '../../import/ImportWizardContext'
import { useAsync } from '../../hooks/useAsync'
import { ImportSettingsModal } from './ImportSettingsModal'
import type { GoogleSheetFile } from '../../api/googleSheets'

/* Step 2, ported from "batchbooks_import_step_1_synced" and
   "batchbooks_import_google_sheets_selection" (the folder names are off by one
   — that first one depicts Upload, not Select Type).

   Both tabs are live. Google Sheets asks the API whether the tenant has a Google
   consent; when it does, the URL box, the browse button and the recent-spreadsheets
   table all work, and a chosen sheet is ingested through the same path a local file
   takes, so mapping onward is identical. When it does not, the controls stay
   disabled behind a real "Connect Google Sheets" button rather than looking
   functional while doing nothing. */

/** The Google consent callback's outcome, carried in router state after the redirect. */
interface GoogleOutcome {
  result: 'connected' | 'error'
  reason?: string | null
}

/** Shape of the router state the strip-the-query navigation carries. */
interface ImportUploadState {
  google?: GoogleOutcome
}

export function ImportSelectFilePage() {
  const wizard = useImportWizard()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const fileInput = useRef<HTMLInputElement>(null)
  const [source, setSource] = useState<'file' | 'sheets'>('file')
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)

  // Google Sheets state.
  const [sheetUrl, setSheetUrl] = useState('')
  const [importing, setImporting] = useState(false)
  const [sheetError, setSheetError] = useState<string | null>(null)
  const [connecting, setConnecting] = useState(false)

  // The consent round trip leaves the SPA, so this page remounts with the
  // callback's query string attached (?google=connected or ?google=error&reason=…).
  //
  // The outcome is carried forward in `location.state` rather than read from the
  // live query string. The query has to be stripped after the first render — it
  // describes a one-time event, and leaving it there means every reload re-reports
  // it — but reading the tab from a value that is about to be deleted would flip
  // the user back to Local File on the very next render.
  const googleOutcome = useMemo<GoogleOutcome | null>(() => {
    const fromState = (location.state as ImportUploadState | null)?.google
    if (fromState) return fromState

    // First render after the redirect: the query is the only source.
    const result = searchParams.get('google')
    return result === 'connected' || result === 'error'
      ? { result, reason: searchParams.get('reason') }
      : null
  }, [location.state, searchParams])

  const googleStatus = useAsync(() => googleSheetsApi.status(), [])
  const connected = googleStatus.data?.connected ?? false

  // The recent list is only fetched once there is a consent to fetch it with.
  const recent = useAsync(
    () => (connected ? googleSheetsApi.spreadsheets() : Promise.resolve([])),
    [connected],
  )

  const refreshGoogle = googleStatus.reload
  const refreshRecent = recent.reload
  const hasOutcome = googleOutcome !== null

  useEffect(() => {
    if (!hasOutcome) return

    // The status was already re-read by the remount; this covers a same-page
    // return (a mock callback, or a browser that restored the SPA in place).
    refreshGoogle()
    refreshRecent()

    // Drop the query, carrying the outcome in state so it survives this replace.
    navigate('/app/import/upload', { replace: true, state: { google: googleOutcome } })
  }, [hasOutcome, googleOutcome, refreshGoogle, refreshRecent, navigate])

  const accept = useCallback(
    async (file: File) => {
      setUploading(true)
      setError(null)
      try {
        wizard.setUpload(await importApi.upload(file))
      } catch (cause: unknown) {
        setError(cause instanceof ApiError ? cause.message : 'Upload failed. Check the file and try again.')
      } finally {
        setUploading(false)
      }
    },
    [wizard],
  )

  const connectGoogle = async () => {
    setConnecting(true)
    setSheetError(null)
    try {
      const { url } = await googleSheetsApi.connectUrl()
      // Consent happens on Google, so hand the browser over. It returns to this
      // page with ?google=connected, which re-reads the status on load.
      window.location.assign(url)
    } catch (cause: unknown) {
      setSheetError(
        cause instanceof ApiError ? cause.message : 'Could not start the Google connection.',
      )
      setConnecting(false)
    }
  }

  const disconnectGoogle = async () => {
    setSheetError(null)
    try {
      await googleSheetsApi.disconnect()
      googleStatus.reload()
      recent.reload()
    } catch (cause: unknown) {
      setSheetError(cause instanceof ApiError ? cause.message : 'Could not disconnect Google.')
    }
  }

  /** Ingests a sheet by id (from a URL or a recent-list row) into the wizard. */
  const ingest = async (spreadsheetId: string, tab?: string) => {
    setImporting(true)
    setSheetError(null)
    try {
      wizard.setUpload(await googleSheetsApi.importSheet({ spreadsheetId, tab }))
    } catch (cause: unknown) {
      setSheetError(cause instanceof ApiError ? cause.message : 'Could not read that spreadsheet.')
    } finally {
      setImporting(false)
    }
  }

  const importFromUrl = () => {
    const id = parseSpreadsheetId(sheetUrl)
    if (!id) {
      setSheetError('That does not look like a Google Sheets link. Paste the URL from the address bar.')
      return
    }
    void ingest(id)
  }

  const upload = wizard.upload

  // Returning from the consent flow lands on the Sheets tab — where the user was
  // when they left. `googleOutcome` is stable across the strip-the-query
  // navigation, so this stays true for the life of the page rather than only
  // until the query disappears.
  const googleError =
    googleOutcome?.result === 'error'
      ? googleOutcome.reason === 'missing_code'
        ? 'Google did not return an authorization code. Please try again.'
        : googleOutcome.reason === 'invalid_state'
          ? 'That connection request was invalid or expired. Please try again.'
          : 'Could not connect to Google. The consent was declined or expired.'
      : null

  const tab = googleOutcome ? 'sheets' : source
  const sheetMessage = sheetError ?? googleError

  // Step 2 is meaningless without the step 1 choice, and the wizard state is
  // in memory only — a reload or a deep link lands here with nothing set. The
  // entity type is restored from sessionStorage, so a value that no longer
  // matches a known entity (a stale draft from an older catalogue) is treated
  // as no selection rather than rendering a page with a dangling subject.
  const knownEntity = wizard.entities.some((e) => e.name === wizard.entityType)
  if (!wizard.entityType || !knownEntity) return <Navigate to="/app/import" replace />

  return (
    <div className="bb bb-wizard">
      <BbStepper steps={IMPORT_STEPS} current={1} />

      <div className="bb-panel-card">
        <div className="bb-panel-card-b">
          <h2>Import Data</h2>
          <p className="bb-map-help" style={{ marginBottom: 20 }}>
            Importing <b>{wizard.entity?.label ?? wizard.entityType}</b> into QuickBooks.
          </p>

          <div className="bb-tabs" role="tablist" aria-label="Select the source of your data.">
            <button
              className={tab === 'file' ? 'bb-tab bb-tab-on' : 'bb-tab'}
              type="button"
              role="tab"
              aria-selected={tab === 'file'}
              onClick={() => setSource('file')}
            >
              Local File
            </button>
            <button
              className={tab === 'sheets' ? 'bb-tab bb-tab-on' : 'bb-tab'}
              type="button"
              role="tab"
              aria-selected={tab === 'sheets'}
              onClick={() => setSource('sheets')}
            >
              Google Sheets
            </button>
          </div>

          {tab === 'file' ? (
            <>
              <div
                className={dragging ? 'bb-drop bb-drop-on' : 'bb-drop'}
                role="button"
                tabIndex={0}
                onClick={() => fileInput.current?.click()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    fileInput.current?.click()
                  }
                }}
                onDragOver={(event) => {
                  event.preventDefault()
                  setDragging(true)
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => {
                  event.preventDefault()
                  setDragging(false)
                  const file = event.dataTransfer.files[0]
                  if (file) void accept(file)
                }}
              >
                <BbIcon name="cloud-upload" />
                <span className="bb-drop-t">
                  {uploading ? 'Uploading…' : 'Drag and drop file here'}
                </span>
                <span className="bb-drop-s">
                  or click to browse (.csv, .xls, .xlsx, .xlsm, .txt — max 10MB)
                </span>
              </div>

              {error ? (
                <Alert tone="err" title="Upload failed" style={{ marginTop: 18 }}>
                  {error}
                </Alert>
              ) : null}
            </>
          ) : (
            <>
              {googleStatus.loading ? (
                <p className="bb-map-help">Checking your Google connection…</p>
              ) : connected ? (
                <>
                  <div className="bb-sheets-conn">
                    <BbIcon name="check-circle" />
                    <span>
                      Connected to Google
                      {googleStatus.data?.email ? ` as ${googleStatus.data.email}` : ''}
                    </span>
                    <span className="spacer" />
                    <button className="bb-clear" type="button" onClick={() => void disconnectGoogle()}>
                      Disconnect
                    </button>
                  </div>

                  <div className="bb-field" style={{ marginTop: 20 }}>
                    <label className="bb-flabel" htmlFor="gsUrl">
                      Paste Google Sheet URL
                    </label>
                    <div className="bb-inline">
                      <input
                        className="bb-input"
                        id="gsUrl"
                        type="url"
                        value={sheetUrl}
                        placeholder="https://docs.google.com/spreadsheets/d/..."
                        onChange={(event) => setSheetUrl(event.target.value)}
                      />
                      <button
                        className="bb-btn bb-btn-navy"
                        type="button"
                        disabled={importing || !sheetUrl.trim()}
                        onClick={importFromUrl}
                      >
                        {importing ? 'Reading…' : 'Import'}
                      </button>
                    </div>
                  </div>

                  <div className="bb-rule-or">or select directly from your account</div>

                  <h3 className="bb-sub-h">Recent Spreadsheets</h3>

                  {recent.loading ? (
                    <p className="bb-map-help">Loading your spreadsheets…</p>
                  ) : recent.error ? (
                    <Alert tone="warn" title="Could not list your spreadsheets">
                      {recent.error.message}
                    </Alert>
                  ) : (recent.data?.length ?? 0) === 0 ? (
                    <p className="bb-map-help">
                      No spreadsheets found in this Google account. Paste a URL above instead.
                    </p>
                  ) : (
                    <table className="bb-rtable">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Last Modified</th>
                          <th className="bb-a-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(recent.data ?? []).map((file: GoogleSheetFile) => (
                          <tr key={file.id}>
                            <td>
                              <span className="bb-pipe-left">
                                <BbIcon name="sheet" />
                                {file.name}
                              </span>
                            </td>
                            <td className="bb-a-time">
                              {file.modifiedAt ? formatRelative(file.modifiedAt) : '—'}
                            </td>
                            <td className="bb-a-right">
                              <button
                                className="bb-alink"
                                type="button"
                                disabled={importing}
                                onClick={() => void ingest(file.id)}
                              >
                                Select
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              ) : (
                <>
                  <Alert tone="info" title="Connect a Google account">
                    Importing from Google Sheets reads a spreadsheet you own. Connect an account and
                    you can paste a sheet link or pick one of your recent files.
                  </Alert>

                  {sheetMessage ? (
                    <Alert tone="err" title="Could not connect" style={{ marginTop: 18 }}>
                      {sheetMessage}
                    </Alert>
                  ) : null}

                  <button
                    className="bb-btn bb-btn-green"
                    type="button"
                    style={{ marginTop: 18 }}
                    disabled={connecting}
                    onClick={() => void connectGoogle()}
                  >
                    <BbIcon name="drive" />
                    {connecting ? 'Opening Google…' : 'Connect Google Sheets'}
                  </button>

                  <h3 className="bb-sub-h" style={{ marginTop: 28 }}>
                    Recent Spreadsheets
                  </h3>
                  <p className="bb-map-help">
                    Connect an account to see your recent spreadsheets here.
                  </p>
                </>
              )}

              {/* A read of a sheet is the same result as a file upload, so the
                  banner below is the shared "what did we ingest" summary. */}
              {sheetMessage && connected ? (
                <Alert tone="err" title="Could not read that spreadsheet" style={{ marginTop: 18 }}>
                  {sheetMessage}
                </Alert>
              ) : null}
            </>
          )}

          {upload ? (
            <>
              <div className="bb-file">
                <BbIcon name="sheet" />
                <span style={{ flex: 1 }}>
                  <span className="bb-file-n">{upload.fileName}</span>
                  <span className="bb-file-m">
                    {formatBytes(upload.sizeBytes)} · {formatInt(upload.sheets.length)} sheets ·{' '}
                    {formatInt(upload.totalRows)} data rows
                  </span>
                </span>
                <button
                  className="bb-btn bb-btn-quiet"
                  type="button"
                  onClick={() => wizard.setUpload(null)}
                >
                  Remove
                </button>
              </div>

              {upload.sheets.length > 1 ? (
                <div className="bb-field" style={{ marginTop: 20, maxWidth: 400 }}>
                  <label className="bb-flabel" htmlFor="sheetSel">
                    Which sheet holds the data?
                  </label>
                  <select
                    className="bb-select"
                    id="sheetSel"
                    value={wizard.sheet ?? upload.sheets[0]}
                    onChange={(event) => {
                      const selected = event.target.value
                      wizard.setSheet(selected)
                      // Re-parse the file against the chosen sheet so upload.headers
                      // reflects the new sheet's columns.
                      importApi.reparse(upload.uploadId, selected).then((result) => {
                        // Preserve the original sheets list and uploadId
                        wizard.setUpload({ ...result, sheets: upload.sheets })
                        wizard.setSheet(selected)
                      }).catch(() => {
                        // Silently fall back — the old headers stay until next step
                      })
                    }}
                  >
                    {upload.sheets.map((sheet) => (
                      <option value={sheet} key={sheet}>
                        {sheet}
                      </option>
                    ))}
                  </select>
                  <div className="bb-map-help">Row 1 is always treated as the header row.</div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      <div className="bb-wizard-foot">
        <Link className="bb-btn bb-btn-quiet" to="/app/import">
          <BbIcon name="arrow-left" />
          Previous Step
        </Link>
        <span className="bb-wizard-foot-r">
          <button className="bb-btn bb-btn-quiet" type="button" onClick={() => setSettingsOpen(true)}>
            <BbIcon name="tune" />
            Settings
          </button>
          <button
            className="bb-btn bb-btn-green"
            type="button"
            disabled={!upload}
            onClick={() => navigate('/app/import/map')}
          >
            Next Step
            <BbIcon name="arrow-right" />
          </button>
        </span>
      </div>

      <input
        ref={fileInput}
        type="file"
        hidden
        accept=".xls,.xlsx,.xlsm,.csv,.txt"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) void accept(file)
          event.target.value = ''
        }}
      />

      <ImportSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  )
}
